/**
 * Select - Reusable select component
 * Wrapper around Material-UI Select with consistent styling
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  MenuItem,
  FormHelperText
} from '@mui/material';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<MuiSelectProps, 'children'> {
  options: SelectOption[];
  label?: string;
  helperText?: string;
  error?: boolean;
}

export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  ({
    options,
    label,
    helperText,
    error,
    ...props
  }, ref) => {
    return (
      <FormControl fullWidth error={error} ref={ref}>
        {label && <InputLabel>{label}</InputLabel>}
        <MuiSelect
          {...props}
          label={label}
          sx={{
            borderRadius: '6px',
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#14B8A6'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#14B8A6'
              }
            },
            ...props.sx
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

Select.displayName = 'Select';

export default Select;
